import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetch2,
  fetch3,
  fetch4,
  fetch5,
  fetch7,
  fetch8,
} from "../commonApis/commonApis";
import { addDriver, setDriverList } from "../features/driver/driverSlice";
import { useDispatch } from "react-redux";
const url = process.env.REACT_APP_BASE_URL;

type DriverData = {
  name: string;
  mobileNo: string;
  email: string;
  licenseNumber: string;
  licenseExpiry: string;
  countryId: string;
  cityId: string;
  image: string;
  document: string;
  status?: string;
};

type DriverStatus = {
  status: string;
};

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
  statusCode?: number;
  statusMessage?: string;
  error?: any;
}

export const useFetchDriversAllData = (searchFor: string) => {
  return useQuery({
    queryKey: ["driversAllData"], // include country in key
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/driver/all?searchFor=${searchFor}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    // enabled: !!country, // ❗️Don't run until a country is selected
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchDriversAllDataForPickupMerchant = (
  clientId?: string,
  warehouseId?: string,
) => {
  return useQuery({
    queryKey: ["driversAllDataForPickupMerchant", clientId, warehouseId], // include country in key
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/driver/all?client=${clientId}&warehouse=${warehouseId ?? ""}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    // enabled: !!country, // ❗️Don't run until a country is selected
    staleTime: 1000 * 60 * 5,
  });
};

export const useAssignRoute = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/driver/${payload?.id}/assign-route`,
        "PATCH",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "❌ Assign route request failed";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Assign route successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Assign route request failed:", error.message);
    },
  });
};

export const useFetchDriversByWarehouse = (
  warehouseId?: string,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: ["driversByWarehouse", warehouseId],

    queryFn: async () => {
      if (!warehouseId) return [];

      const res = await fetch3(
        `${url}/api/driver/warehouse/${warehouseId}`,
        "get",
      );

      if (!res || !res.data) return [];

      return res.data;
    },

    // ✅ IMPORTANT FIX
    enabled: !!warehouseId,

    staleTime: 1000 * 60 * 5,
  });
};

export const useDriverCreateMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: DriverData) => {
      const res: ApiResponse = await fetch2(`${url}/api/driver`, payload);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || res?.error || "Something went wrong";
      }

      return res;
    },
    onSuccess: (data: any) => {
      console.log("✅ Driver Created successfully:", data);
      dispatch(addDriver(data));

      // Invalidate and refetch driver list queries to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ["driverList"] });
    },
    onError: (error: any) => {
      console.error("❌ Driver create request failed:", error.message);
      return error;
    },
  });
};

export const useDriverListData = (
  page: number,
  limit: number,
  searchFor: string,
) => {
  const dispatch = useDispatch();
  return useQuery({
    queryKey: ["driverList", page, limit, searchFor],
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/driver?page=${page}&limit=${limit}&searchFor=${searchFor}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      dispatch(setDriverList(res.data));
      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useDriverUpdateMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: DriverData) => {
      const res: ApiResponse = await fetch7(
        `${url}/api/driver/${id}`,
        "PUT",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || res?.error || "Something went wrong";
      }

      return res;
    },
    onSuccess: (data: any) => {
      console.log("✅ Driver Update successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Driver Update request failed:", error.message);
      return error;
    },
  });
};

export const useDriverStatusMutation = (id: string) => {
  return useMutation({
    mutationFn: async (payload: DriverStatus) => {
      const res: ApiResponse = await fetch5(
        `${url}/api/driver/${id}/status`,
        "PATCH",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Staff Created successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ staff create request failed:", error.message);
    },
  });
};

export const useFetchRouteDriversData = (routeCode: string) => {
  return useQuery({
    queryKey: ["routeDriversAllData", routeCode], // include country in key
    queryFn: async () => {
      const res = await fetch3(
        `${url}/api/driver/by-route/${routeCode}`,
        "get",
      );

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    enabled: !!routeCode, // ❗️Don't run until a country is selected
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchDriverDetailsData = (id?: string) => {
  return useQuery({
    queryKey: ["driverDetailsData", id],
    queryFn: async () => {
      if (!id) return null; // 👈 early exit inside the hook logic
      const res = await fetch3(`${url}/api/driver/${id}?type=LM`, "get");
      if (!res || !res.data) return res.message;
      return res.data;
    },
    enabled: !!id, // 👈 run only when id exists
    staleTime: 1000 * 60 * 5,
  });
};

export const useDriverDeleteMutation = (id: string) => {
  return useMutation({
    mutationFn: async () => {
      const res: ApiResponse = await fetch5(
        `${url}/api/driver/${id}`,
        "DELETE",
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log("✅ Driver deleted successfully:", data);
    },
    onError: (error: any) => {
      console.error("❌ Driver delete request failed:", error.message);
    },
  });
};

export const useDriverBulkDocMutation = () => {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res: ApiResponse = await fetch8(
        `${url}/api/bulk/bulk-driver`,
        "POST",
        payload,
      );

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || "Something went wrong";
      }

      return res;
    },
    onSuccess: (res: any) => {
      return res;
    },
    onError: (error: any) => {
      console.error("❌ Drivers create request failed:", error.message);
    },
  });
};

export const useFetchDriverTemplate = () => {
  return useQuery({
    queryKey: ["templates"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/templates/drivers`, "get");
      if (!res || !res.data) return res.message;
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

// search driver by name or code
export const useSearchDriver = (searchTerm: string) => {
  return useQuery({
    queryKey: ["searchDriver", searchTerm],
    queryFn: async () => {
      if (!searchTerm?.trim()) return [];
      console.log("🔍 API call for search term:", searchTerm);
      const res = await fetch4(`${url}/api/driver/current-locations`, {
        search: searchTerm.trim(),
      });
      console.log("📡 Search API response:", res);
      if (!res || !res.success) {
        console.warn("⚠️ Search API failed:", res?.message || res?.error);
        return [];
      }
      return res.data || [];
    },
    enabled: !!searchTerm?.trim(),
    staleTime: 1000 * 30, // Shorter cache time for search results
    refetchOnWindowFocus: false,
  });
};
